import { supabase } from "./supabase"
import type { Database } from "@/types/database"
import type { User, Post, Comment, Vote, Tag, Category, PostWithAuthor, Notification, NotificationWithActor, CommentWithAuthor, Job, JobWithAuthor } from "@/types"

type DbUser = Database["public"]["Tables"]["users"]["Row"]
type DbPost = Database["public"]["Tables"]["posts"]["Row"]
type DbComment = Database["public"]["Tables"]["comments"]["Row"]
type DbVote = Database["public"]["Tables"]["votes"]["Row"]
type DbTag = Database["public"]["Tables"]["tags"]["Row"]
type DbCategory = Database["public"]["Tables"]["categories"]["Row"]
type DbJob = Database["public"]["Tables"]["jobs"]["Row"]
let canCreateTags = true

export function isNetworkFetchError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false
  const maybeMessage = "message" in error ? String((error as { message?: unknown }).message || "") : ""
  const maybeDetails = "details" in error ? String((error as { details?: unknown }).details || "") : ""
  const combined = `${maybeMessage} ${maybeDetails}`.toLowerCase()
  return combined.includes("failed to fetch") || combined.includes("networkerror") || combined.includes("network request failed")
}

function isPermissionError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false
  const status = "status" in error ? Number((error as { status?: unknown }).status) : NaN
  const code = "code" in error ? String((error as { code?: unknown }).code || "") : ""
  return status === 401 || status === 403 || code === "42501"
}

// User operations
export async function getUserById(uid: string): Promise<User | null> {
  const { data, error } = await supabase.from("users").select("*").eq("id", uid).single()

  if (error) {
    if (error.code === "PGRST116") {
      return null
    }
    throw error
  }

  return dbUserToUser(data)
}

export async function getUserByUsername(username: string): Promise<User | null> {
  const { data, error } = await supabase.from("users").select("*").eq("username", username).single()

  if (error) {
    if (error.code === "PGRST116") return null
    throw error
  }

  return dbUserToUser(data)
}

export async function updateUser(uid: string, data: Partial<User>): Promise<void> {
  const { error } = await supabase
    .from("users")
    .update({
      display_name: data.displayName,
      bio: data.bio,
      website: data.website,
      twitter: data.twitter,
      github: data.github,
      linkedin: data.linkedin,
      avatar_url: data.avatarUrl,
    })
    .eq("id", uid)

  if (error) throw error
}

// Sync OAuth profile data (avatar, display name) on each login
export async function syncOAuthProfile(uid: string, metadata: Record<string, string | undefined>): Promise<void> {
  const avatarUrl = metadata.avatar_url || metadata.picture
  const displayName = metadata.display_name || metadata.full_name || metadata.name

  if (!avatarUrl && !displayName) return

  const updateData: Record<string, string> = {}
  if (avatarUrl) updateData.avatar_url = avatarUrl
  if (displayName) updateData.display_name = displayName

  const { error } = await supabase.from("users").update(updateData).eq("id", uid)

  if (error) {
    console.error("Failed to sync OAuth profile:", error)
  }
}

// Post operations
export async function getPostById(id: string): Promise<Post | null> {
  const { data, error } = await supabase.from("posts").select("*").eq("id", id).single()

  if (error) {
    if (error.code === "PGRST116") return null
    throw error
  }

  const post = dbPostToPost(data)
  try {
    const tagMap = await loadPostTags([post.id])
    return { ...post, tags: tagMap.get(post.id) || [] }
  } catch {
    return post
  }
}

export async function getPostBySlug(slug: string): Promise<Post | null> {
  const { data, error } = await supabase.from("posts").select("*").eq("slug", slug).eq("status", "published").single()

  if (error) {
    if (error.code === "PGRST116") return null
    throw error
  }

  return dbPostToPost(data)
}

export async function getUniqueSlug(baseSlug: string, excludePostId?: string): Promise<string> {
  let suffix = 0
  while (suffix < 200) {
    const candidate = suffix === 0 ? baseSlug : `${baseSlug}-post-${suffix + 1}`
    const { data, error } = await supabase.from("posts").select("id").eq("slug", candidate).maybeSingle()

    if (error && error.code !== "PGRST116") throw error
    if (!data || data.id === excludePostId) return candidate
    suffix += 1
  }

  return `${baseSlug}-${Date.now()}`
}

export async function getPosts(options: {
  category?: string
  tag?: string
  authorId?: string
  status?: "draft" | "published" | "archived"
  orderByField?: string
  orderDirection?: "asc" | "desc"
  limitCount?: number
  offset?: number
}): Promise<{ posts: Post[]; hasMore: boolean }> {
  let query = supabase.from("posts").select("*")

  if (options.category) query = query.eq("category_id", options.category)
  if (options.authorId) query = query.eq("author_id", options.authorId)
  if (options.status) query = query.eq("status", options.status)

  const orderField = options.orderByField || "published_at"
  const orderDir = options.orderDirection || "desc"
  query = query.order(orderField, { ascending: orderDir === "asc" })

  const limitCount = options.limitCount || 10
  query = query.limit(limitCount + 1) // Fetch one extra to check if there are more

  if (options.offset) query = query.range(options.offset, options.offset + limitCount)

  const { data, error } = await query

  if (error) throw error

  const hasMore = data.length > limitCount
  const posts = (hasMore ? data.slice(0, -1) : data).map(dbPostToPost)

  return { posts, hasMore }
}

export async function getPostsWithAuthors(posts: Post[]): Promise<PostWithAuthor[]> {
  const authorIds = [...new Set(posts.map(post => post.authorId))]
  const authors = await Promise.all(authorIds.map(id => getUserById(id)))
  const authorMap = new Map(authors.filter(Boolean).map(author => [author!.uid, author!]))

  return posts.map(post => ({
    ...post,
    author: authorMap.get(post.authorId) || createFallbackUser(post.authorId),
  }))
}

export async function createPost(data: Omit<Post, "id" | "createdAt" | "updatedAt">): Promise<string> {
  const { data: post, error } = await supabase
    .from("posts")
    .insert({
      author_id: data.authorId,
      title: data.title,
      subtitle: data.subtitle,
      slug: data.slug,
      content: data.content,
      excerpt: data.excerpt,
      cover_image: data.coverImage,
      category_id: data.category || null,
      status: data.status,
      reading_time: data.readingTime,
      published_at: data.publishedAt || null,
    })
    .select("id")
    .single()

  if (error) throw error
  await syncPostTags(post.id, data.tags || [])
  return post.id
}

export async function updatePost(id: string, data: Partial<Post>): Promise<void> {
  const updateData: Record<string, unknown> = {}

  if (data.title !== undefined) updateData.title = data.title
  if (data.subtitle !== undefined) updateData.subtitle = data.subtitle
  if (data.slug !== undefined) updateData.slug = data.slug
  if (data.content !== undefined) updateData.content = data.content
  if (data.excerpt !== undefined) updateData.excerpt = data.excerpt
  if (data.coverImage !== undefined) updateData.cover_image = data.coverImage
  if (data.category !== undefined) updateData.category_id = data.category || null
  if (data.status !== undefined) updateData.status = data.status
  if (data.readingTime !== undefined) updateData.reading_time = data.readingTime
  if (data.publishedAt !== undefined) updateData.published_at = data.publishedAt || null

  const { error } = await supabase.from("posts").update(updateData).eq("id", id)

  if (error) throw error

  if (data.tags !== undefined) {
    await syncPostTags(id, data.tags)
  }
}

export async function deletePost(id: string): Promise<void> {
  const { error } = await supabase.from("posts").delete().eq("id", id)
  if (error) throw error
}

export async function incrementPostView(id: string): Promise<void> {
  const { error } = await supabase.rpc("increment_post_views", { post_id: id })
  if (error) throw error
}

// Comment operations
export async function getCommentsByPostId(postId: string): Promise<Comment[]> {
  const { data, error } = await supabase
    .from("comments")
    .select("*")
    .eq("post_id", postId)
    .eq("status", "visible")
    .order("created_at", { ascending: true })

  if (error) throw error
  return data.map(dbCommentToComment)
}

export async function createComment(data: Omit<Comment, "id" | "createdAt" | "updatedAt">): Promise<string> {
  const { data: comment, error } = await supabase
    .from("comments")
    .insert({
      post_id: data.postId,
      author_id: data.authorId,
      parent_id: data.parentId,
      content: data.content,
    })
    .select("id")
    .single()

  if (error) throw error

  // Create notification for post author or parent comment author
  try {
    if (data.parentId) {
      // Reply to comment - notify the parent comment author
      const { data: parentComment } = await supabase.from("comments").select("author_id").eq("id", data.parentId).single()
      if (parentComment) {
        await createNotification(parentComment.author_id, data.authorId, "reply", data.postId, "post", data.content.substring(0, 100))
      }
    } else {
      // Comment on post - notify the post author
      const { data: post } = await supabase.from("posts").select("author_id").eq("id", data.postId).single()
      if (post) {
        await createNotification(post.author_id, data.authorId, "comment", data.postId, "post", data.content.substring(0, 100))
      }
    }
  } catch (notifError) {
    console.error("Failed to create notification:", notifError)
  }

  return comment.id
}

export async function updateComment(commentId: string, content: string): Promise<void> {
  const { error } = await supabase.from("comments").update({ content }).eq("id", commentId)

  if (error) throw error
}

export async function deleteComment(commentId: string): Promise<void> {
  const { error } = await supabase.from("comments").update({ status: "deleted" }).eq("id", commentId)

  if (error) throw error
}

// Follow operations
export async function followUser(followerId: string, followingId: string): Promise<void> {
  const { error } = await supabase.from("follows").insert({
    follower_id: followerId,
    following_id: followingId,
  })

  if (error) throw error

  // Create notification for the user being followed
  try {
    await createNotification(followingId, followerId, "follow")
  } catch (notifError) {
    console.error("Failed to create notification:", notifError)
  }
}

export async function unfollowUser(followerId: string, followingId: string): Promise<void> {
  const { error } = await supabase.from("follows").delete().eq("follower_id", followerId).eq("following_id", followingId)

  if (error) throw error
}

export async function isFollowing(followerId: string, followingId: string): Promise<boolean> {
  const { data, error } = await supabase.from("follows").select("follower_id").eq("follower_id", followerId).eq("following_id", followingId).single()

  if (error) {
    if (error.code === "PGRST116") return false // Not found
    throw error
  }

  return !!data
}

// Vote operations
export async function getVote(userId: string, targetId: string): Promise<Vote | null> {
  const { data, error } = await supabase.from("votes").select("*").eq("user_id", userId).eq("target_id", targetId).single()

  if (error) {
    if (error.code === "PGRST116") return null
    throw error
  }

  return dbVoteToVote(data)
}

export async function voteOnTarget(userId: string, targetId: string, targetType: "post" | "comment", voteType: "upvote" | "downvote"): Promise<void> {
  // Check if vote already exists
  const existingVote = await getVote(userId, targetId)

  if (existingVote) {
    // If same vote type, remove the vote (toggle off)
    if ((existingVote.value === 1 && voteType === "upvote") || (existingVote.value === -1 && voteType === "downvote")) {
      const { error } = await supabase.from("votes").delete().eq("user_id", userId).eq("target_id", targetId)
      if (error) throw error
    } else {
      // Different vote type, update it
      const { error } = await supabase.from("votes").update({ vote_type: voteType }).eq("user_id", userId).eq("target_id", targetId)
      if (error) throw error
    }
  } else {
    // No existing vote, create new one
    const { error } = await supabase.from("votes").insert({
      user_id: userId,
      target_id: targetId,
      target_type: targetType,
      vote_type: voteType,
    })
    if (error) throw error
  }
}

// Tag operations
export async function getTags(): Promise<Tag[]> {
  const { data, error } = await supabase.from("tags").select("*").order("post_count", { ascending: false }).limit(20)

  if (error) throw error
  return data.map(dbTagToTag)
}

function normalizeTagName(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
}

function slugifyTag(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
}

async function getOrCreateTagIds(tagNames: string[]): Promise<string[]> {
  const normalized = [...new Set(tagNames.map(normalizeTagName).filter(Boolean))]
  if (!normalized.length) return []

  const slugs = normalized.map(slugifyTag)
  const { data: existingTags, error: existingError } = await supabase.from("tags").select("id, slug").in("slug", slugs)
  if (existingError) throw existingError

  const existingBySlug = new Map(existingTags.map(tag => [tag.slug, tag.id]))
  const missing = normalized.filter(name => !existingBySlug.has(slugifyTag(name)))

  if (missing.length) {
    if (canCreateTags) {
      const inserts = missing.map(name => ({ name, slug: slugifyTag(name) }))
      const { error: insertError } = await supabase.from("tags").insert(inserts)
      if (insertError && !isNetworkFetchError(insertError)) {
        // If the DB policy does not allow creating tags, keep existing tags instead of failing post save.
        if (isPermissionError(insertError)) canCreateTags = false
        console.warn("Could not create some tags:", insertError.message)
      }
    }
  }

  const { data: allTags, error: allError } = await supabase.from("tags").select("id, slug").in("slug", slugs)
  if (allError) throw allError

  return allTags.map(tag => tag.id)
}

async function syncPostTags(postId: string, tagNames: string[]): Promise<void> {
  const tagIds = await getOrCreateTagIds(tagNames)

  const { error: deleteError } = await supabase.from("post_tags").delete().eq("post_id", postId)
  if (deleteError) throw deleteError

  if (!tagIds.length) return

  const rows = tagIds.map(tagId => ({ post_id: postId, tag_id: tagId }))
  const { error: insertError } = await supabase.from("post_tags").insert(rows)
  if (insertError) throw insertError
}

// Category operations
export async function getCategories(): Promise<Category[]> {
  const { data, error } = await supabase.from("categories").select("*").order("order", { ascending: true })

  if (error) throw error
  return data.map(dbCategoryToCategory)
}

export async function getCategoryBySlug(slug: string): Promise<Category | null> {
  const { data, error } = await supabase.from("categories").select("*").eq("slug", slug).single()

  if (error) {
    if (error.code === "PGRST116") return null
    throw error
  }

  return dbCategoryToCategory(data)
}

// Search operations
export async function searchPosts(query: string, limitCount = 20): Promise<Post[]> {
  const { data, error } = await supabase
    .from("posts")
    .select("*")
    .eq("status", "published")
    .textSearch("search_vector", query, { type: "websearch" })
    .limit(limitCount)

  if (error) throw error
  return data.map(dbPostToPost)
}

// Tag-based post queries
export async function getPostsByTag(tagSlug: string, limitCount = 20): Promise<Post[]> {
  // First get the tag ID
  const { data: tag, error: tagError } = await supabase.from("tags").select("id").eq("slug", tagSlug).single()

  if (tagError) {
    if (tagError.code === "PGRST116") return []
    throw tagError
  }

  // Get post IDs from junction table
  const { data: postTags, error: ptError } = await supabase.from("post_tags").select("post_id").eq("tag_id", tag.id)

  if (ptError) throw ptError
  if (!postTags.length) return []

  const postIds = postTags.map(pt => pt.post_id)

  // Fetch the actual posts
  const { data: posts, error: postsError } = await supabase
    .from("posts")
    .select("*")
    .in("id", postIds)
    .eq("status", "published")
    .order("published_at", { ascending: false })
    .limit(limitCount)

  if (postsError) throw postsError
  return posts.map(dbPostToPost)
}

export async function getTagBySlug(tagSlug: string): Promise<Tag | null> {
  const { data, error } = await supabase.from("tags").select("*").eq("slug", tagSlug).single()

  if (error) {
    if (error.code === "PGRST116") return null
    throw error
  }

  return dbTagToTag(data)
}

// Load tags for multiple posts (batch)
export async function loadPostTags(postIds: string[]): Promise<Map<string, string[]>> {
  if (!postIds.length) return new Map()

  const { data: postTags, error: ptError } = await supabase.from("post_tags").select("post_id, tag_id").in("post_id", postIds)

  if (ptError) throw ptError
  if (!postTags.length) return new Map()

  const tagIds = [...new Set(postTags.map(pt => pt.tag_id))]
  const { data: tags, error: tagError } = await supabase.from("tags").select("*").in("id", tagIds)

  if (tagError) throw tagError

  const tagMap = new Map(tags.map(t => [t.id, t.name]))
  const result = new Map<string, string[]>()

  for (const pt of postTags) {
    const tagName = tagMap.get(pt.tag_id)
    if (tagName) {
      const existing = result.get(pt.post_id) || []
      existing.push(tagName)
      result.set(pt.post_id, existing)
    }
  }

  return result
}

// Enrich posts with their tags
export async function enrichPostsWithTags<T extends Post>(posts: T[]): Promise<T[]> {
  if (!posts.length) return posts
  const tagMap = await loadPostTags(posts.map(p => p.id))
  return posts.map(post => ({
    ...post,
    tags: tagMap.get(post.id) || [],
  }))
}

// Comments with authors
export async function getCommentsWithAuthors(postId: string): Promise<CommentWithAuthor[]> {
  const comments = await getCommentsByPostId(postId)
  if (!comments.length) return []

  const authorIds = [...new Set(comments.map(c => c.authorId))]
  const authors = await Promise.all(authorIds.map(id => getUserById(id)))
  const authorMap = new Map(authors.filter(Boolean).map(a => [a!.uid, a!]))

  return comments.map(comment => ({
    ...comment,
    author: authorMap.get(comment.authorId) || createFallbackUser(comment.authorId),
  }))
}

// Bookmark operations
export async function getBookmarkedPosts(userId: string): Promise<PostWithAuthor[]> {
  const { data: bookmarks, error } = await supabase
    .from("bookmarks")
    .select("post_id")
    .eq("user_id", userId)
    .order("created_at", { ascending: false })

  if (error) throw error
  if (!bookmarks.length) return []

  const postIds = bookmarks.map(b => b.post_id)
  const { data: posts, error: postsError } = await supabase.from("posts").select("*").in("id", postIds)

  if (postsError) throw postsError

  const mappedPosts = posts.map(dbPostToPost)
  return getPostsWithAuthors(mappedPosts)
}

export async function addBookmark(userId: string, postId: string): Promise<void> {
  const { error } = await supabase.from("bookmarks").insert({ user_id: userId, post_id: postId })
  if (error) throw error
}

export async function removeBookmark(userId: string, postId: string): Promise<void> {
  const { error } = await supabase.from("bookmarks").delete().eq("user_id", userId).eq("post_id", postId)
  if (error) throw error
}

export async function isBookmarked(userId: string, postId: string): Promise<boolean> {
  const { data, error } = await supabase.from("bookmarks").select("post_id").eq("user_id", userId).eq("post_id", postId).single()

  if (error) {
    if (error.code === "PGRST116") return false
    throw error
  }

  return !!data
}

// Notification operations
type DbNotification = Database["public"]["Tables"]["notifications"]["Row"]

function dbNotificationToNotification(dbNotification: DbNotification): Notification {
  return {
    id: dbNotification.id,
    userId: dbNotification.user_id,
    actorId: dbNotification.actor_id,
    type: dbNotification.type as Notification["type"],
    targetId: dbNotification.target_id,
    targetType: dbNotification.target_type as Notification["targetType"],
    content: dbNotification.content || "",
    isRead: dbNotification.read,
    createdAt: dbNotification.created_at,
  }
}

export async function getNotifications(userId: string): Promise<NotificationWithActor[]> {
  const { data, error } = await supabase.from("notifications").select("*").eq("user_id", userId).order("created_at", { ascending: false }).limit(50)

  if (error) throw error

  const notifications = data.map(dbNotificationToNotification)
  if (!notifications.length) return []

  // Load actors
  const actorIds = [...new Set(notifications.map(n => n.actorId).filter(Boolean))] as string[]
  const actors = await Promise.all(actorIds.map(id => getUserById(id)))
  const actorMap = new Map(actors.filter(Boolean).map(a => [a!.uid, a!]))

  // Load post titles for notifications that reference posts
  const postIds = [...new Set(notifications.map(n => n.targetId).filter(Boolean))] as string[]
  const postMap = new Map<string, { title: string; slug: string }>()
  if (postIds.length) {
    const { data: posts } = await supabase.from("posts").select("id, title, slug").in("id", postIds)
    if (posts) {
      for (const p of posts) {
        postMap.set(p.id, { title: p.title, slug: p.slug })
      }
    }
  }

  return notifications.map(n => ({
    ...n,
    actor: n.actorId ? actorMap.get(n.actorId) || null : null,
    postTitle: n.targetId ? postMap.get(n.targetId)?.title : undefined,
    postSlug: n.targetId ? postMap.get(n.targetId)?.slug : undefined,
  }))
}

export async function markNotificationRead(notificationId: string): Promise<void> {
  const { error } = await supabase.from("notifications").update({ read: true }).eq("id", notificationId)
  if (error) throw error
}

export async function markAllNotificationsRead(userId: string): Promise<void> {
  const { error } = await supabase.from("notifications").update({ read: true }).eq("user_id", userId).eq("read", false)
  if (error) throw error
}

export async function getUnreadNotificationCount(userId: string): Promise<number> {
  const { count, error } = await supabase.from("notifications").select("*", { count: "exact", head: true }).eq("user_id", userId).eq("read", false)

  if (error) throw error
  return count || 0
}

export async function createNotification(
  userId: string,
  actorId: string,
  type: "comment" | "reply" | "upvote" | "follow" | "mention",
  targetId?: string,
  targetType?: "post" | "comment",
  content?: string,
): Promise<void> {
  // Don't create notification if user is notifying themselves
  if (userId === actorId) return

  const { error } = await supabase.from("notifications").insert({
    user_id: userId,
    actor_id: actorId,
    type,
    target_id: targetId,
    target_type: targetType,
    content,
  })

  if (error) throw error
}

// Helper functions to convert database types to app types
function dbUserToUser(dbUser: DbUser): User {
  return {
    uid: dbUser.id,
    username: dbUser.username,
    displayName: dbUser.display_name,
    email: dbUser.email,
    avatarUrl: dbUser.avatar_url || "",
    bio: dbUser.bio || "",
    website: dbUser.website || "",
    twitter: dbUser.twitter || "",
    github: dbUser.github || "",
    linkedin: dbUser.linkedin || "",
    reputation: dbUser.reputation,
    role: dbUser.role as "user" | "moderator" | "admin",
    followerCount: dbUser.follower_count,
    followingCount: dbUser.following_count,
    postCount: dbUser.post_count,
    createdAt: dbUser.created_at,
    updatedAt: dbUser.updated_at,
    savedPosts: [],
    bookmarks: [],
  }
}

function dbPostToPost(dbPost: DbPost): Post {
  return {
    id: dbPost.id,
    authorId: dbPost.author_id,
    title: dbPost.title,
    subtitle: dbPost.subtitle || "",
    slug: dbPost.slug,
    content: dbPost.content,
    excerpt: dbPost.excerpt || "",
    coverImage: dbPost.cover_image || "",
    category: dbPost.category_id || "",
    tags: [],
    status: dbPost.status as "draft" | "published" | "archived",
    readingTime: dbPost.reading_time,
    viewCount: dbPost.view_count,
    upvoteCount: dbPost.upvote_count,
    downvoteCount: dbPost.downvote_count,
    commentCount: dbPost.comment_count,
    shareCount: 0,
    publishedAt: dbPost.published_at,
    createdAt: dbPost.created_at,
    updatedAt: dbPost.updated_at,
  }
}

function dbCommentToComment(dbComment: DbComment): Comment {
  return {
    id: dbComment.id,
    postId: dbComment.post_id,
    authorId: dbComment.author_id,
    parentId: dbComment.parent_id ?? null,
    content: dbComment.content,
    upvoteCount: dbComment.upvote_count,
    flagCount: dbComment.downvote_count,
    status: dbComment.status as "visible" | "hidden" | "deleted",
    createdAt: dbComment.created_at,
    updatedAt: dbComment.updated_at,
  }
}

function dbVoteToVote(dbVote: DbVote): Vote {
  return {
    id: dbVote.id,
    userId: dbVote.user_id,
    targetId: dbVote.target_id,
    targetType: dbVote.target_type as "post" | "comment",
    value: dbVote.vote_type === "upvote" ? 1 : -1,
    createdAt: dbVote.created_at,
  }
}

function dbTagToTag(dbTag: DbTag): Tag {
  return {
    id: dbTag.id,
    name: dbTag.name,
    slug: dbTag.slug,
    postCount: dbTag.post_count,
  }
}

function dbCategoryToCategory(dbCategory: DbCategory): Category {
  return {
    id: dbCategory.id,
    name: dbCategory.name,
    slug: dbCategory.slug,
    description: dbCategory.description || "",
    icon: dbCategory.icon || "",
    color: dbCategory.color || "",
    postCount: dbCategory.post_count,
    order: dbCategory.order,
  }
}

function createFallbackUser(uid: string): User {
  return {
    uid,
    username: "unknown",
    displayName: "Unknown User",
    email: "",
    avatarUrl: "",
    bio: "",
    website: "",
    twitter: "",
    github: "",
    linkedin: "",
    reputation: 0,
    role: "user",
    createdAt: "",
    updatedAt: "",
    followerCount: 0,
    followingCount: 0,
    postCount: 0,
    savedPosts: [],
    bookmarks: [],
  }
}

// ==================== Job operations ====================

function dbJobToJob(dbJob: DbJob): Job {
  return {
    id: dbJob.id,
    slug: dbJob.slug,
    authorId: dbJob.author_id,
    title: dbJob.title,
    description: dbJob.description,
    excerpt: dbJob.excerpt || "",
    companyName: dbJob.company_name,
    jobType: dbJob.job_type as Job["jobType"],
    location: dbJob.location,
    isRemote: dbJob.is_remote,
    applyUrl: dbJob.apply_url,
    salaryMin: dbJob.salary_min,
    salaryMax: dbJob.salary_max,
    status: dbJob.status as Job["status"],
    viewCount: dbJob.view_count,
    createdAt: dbJob.created_at,
    updatedAt: dbJob.updated_at,
    expiresAt: dbJob.expires_at,
  }
}

export async function getJobs(options: {
  jobType?: string
  isRemote?: boolean
  authorId?: string
  status?: "draft" | "published" | "closed"
  orderByField?: string
  orderDirection?: "asc" | "desc"
  limitCount?: number
  offset?: number
}): Promise<{ jobs: Job[]; hasMore: boolean }> {
  let query = supabase.from("jobs").select("*")

  if (options.jobType) query = query.eq("job_type", options.jobType as "full-time" | "part-time" | "contract" | "freelance")
  if (options.isRemote !== undefined) query = query.eq("is_remote", options.isRemote)
  if (options.authorId) query = query.eq("author_id", options.authorId)
  if (options.status) query = query.eq("status", options.status)

  const orderField = options.orderByField || "created_at"
  const orderDir = options.orderDirection || "desc"
  query = query.order(orderField, { ascending: orderDir === "asc" })

  const limitCount = options.limitCount || 20
  query = query.limit(limitCount + 1)

  if (options.offset) query = query.range(options.offset, options.offset + limitCount)

  const { data, error } = await query

  if (error) throw error

  const hasMore = data.length > limitCount
  const jobs = (hasMore ? data.slice(0, -1) : data).map(dbJobToJob)

  return { jobs, hasMore }
}

export async function getJobBySlug(slug: string): Promise<Job | null> {
  const { data, error } = await supabase.from("jobs").select("*").eq("slug", slug).single()

  if (error) {
    if (error.code === "PGRST116") return null
    throw error
  }

  return dbJobToJob(data)
}

export async function getJobsWithAuthors(jobs: Job[]): Promise<JobWithAuthor[]> {
  const authorIds = [...new Set(jobs.map(job => job.authorId))]
  const authors = await Promise.all(authorIds.map(id => getUserById(id)))
  const authorMap = new Map(authors.filter(Boolean).map(author => [author!.uid, author!]))

  return jobs.map(job => ({
    ...job,
    author: authorMap.get(job.authorId) || createFallbackUser(job.authorId),
  }))
}

export async function getUniqueJobSlug(baseSlug: string): Promise<string> {
  let suffix = 0
  while (suffix < 200) {
    const candidate = suffix === 0 ? baseSlug : `${baseSlug}-${suffix + 1}`
    const { data, error } = await supabase.from("jobs").select("id").eq("slug", candidate).maybeSingle()

    if (error && error.code !== "PGRST116") throw error
    if (!data) return candidate
    suffix += 1
  }

  return `${baseSlug}-${Date.now()}`
}

export async function createJob(data: Omit<Job, "id" | "createdAt" | "updatedAt" | "viewCount">): Promise<string> {
  const { data: job, error } = await supabase
    .from("jobs")
    .insert({
      author_id: data.authorId,
      title: data.title,
      slug: data.slug,
      company_name: data.companyName,
      description: data.description,
      excerpt: data.excerpt || null,
      job_type: data.jobType,
      location: data.location,
      is_remote: data.isRemote,
      apply_url: data.applyUrl,
      salary_min: data.salaryMin || null,
      salary_max: data.salaryMax || null,
      status: data.status,
      expires_at: data.expiresAt || null,
    })
    .select("id")
    .single()

  if (error) throw error
  return job.id
}

export async function updateJob(id: string, data: Partial<Job>): Promise<void> {
  const updateData: Record<string, unknown> = {}

  if (data.title !== undefined) updateData.title = data.title
  if (data.description !== undefined) updateData.description = data.description
  if (data.excerpt !== undefined) updateData.excerpt = data.excerpt
  if (data.companyName !== undefined) updateData.company_name = data.companyName
  if (data.jobType !== undefined) updateData.job_type = data.jobType
  if (data.location !== undefined) updateData.location = data.location
  if (data.isRemote !== undefined) updateData.is_remote = data.isRemote
  if (data.applyUrl !== undefined) updateData.apply_url = data.applyUrl
  if (data.salaryMin !== undefined) updateData.salary_min = data.salaryMin
  if (data.salaryMax !== undefined) updateData.salary_max = data.salaryMax
  if (data.status !== undefined) updateData.status = data.status

  const { error } = await supabase.from("jobs").update(updateData).eq("id", id)

  if (error) throw error
}

export async function deleteJob(id: string): Promise<void> {
  const { error } = await supabase.from("jobs").delete().eq("id", id)
  if (error) throw error
}

export async function incrementJobView(id: string): Promise<void> {
  const { error } = await supabase.rpc("increment_job_views", { job_id: id })
  if (error) throw error
}
